import { useAuth } from "@clerk/tanstack-react-start";
import { useEffect } from "react";

const REFRESH_INTERVAL_MS = 45_000; // 45 seconds (before the 60s expiry)

/**
 * Keeps Clerk session tokens fresh by periodically calling getToken().
 *
 * Clerk's session tokens expire after 60 seconds. While Clerk.js auto-refreshes
 * on a 50-second interval, this can fail when the browser suspends JavaScript
 * execution during idle periods. This component ensures tokens stay fresh by
 * explicitly refreshing them every 45 seconds.
 */
export function AuthKeepalive(): null {
  const { getToken, isSignedIn } = useAuth();

  useEffect(() => {
    if (!isSignedIn) return;

    const refresh = async (): Promise<void> => {
      try {
        await getToken();
      } catch (e) {
        console.warn("[AuthKeepalive] Failed to refresh token:", e);
      }
    };

    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);

    // When the browser tab is suspended (laptop sleep, tab backgrounded),
    // setInterval stops firing and the Clerk session token can expire.
    // Refresh immediately when the tab becomes visible again.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isSignedIn, getToken]);

  return null;
}
